import { ReactNode } from 'react';
import { useState } from 'react';
import { template } from '@belong/common';
import { IconBank, IconLock } from '@belong/icons';
import { ButtonBase, Spinner, Text } from '@belong/ui';
import clsx from 'clsx';
import { CoverageRegionModel } from 'src/api/models';
import { usePayoutPlans, useRegions } from 'src/features/app';
import { useModal } from 'src/hooks/useModal';
import { RegionSelect } from 'src/modals/region-select/region-select';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import { BLOG_STRINGS } from '../../blog.strings';
import css from './month-year-rent-slice.module.css';

type MonthYearRentSliceProps = {
  singleColumn?: boolean;
};

const MonthYearRentSliceTitles = {
  firstOption: (
    <>
      {'Rent sent on\u00a0the\u00a01st.'}
      <br />
      Guaranteed.
    </>
  ),
  secondOption: (
    <>
      {"A full year's rent."}
      <br />
      All upfront.
    </>
  ),
};

export function MonthYearRentSlice({
  singleColumn = false,
}: MonthYearRentSliceProps): JSX.Element {
  const selectedRegion = useStore(regionSelector);
  const { payoutPlans } = usePayoutPlans();
  const payoutPlan = payoutPlans?.[selectedRegion.id || 1]?.DEFAULT;

  return (
    <div className="w-full">
      <div
        className={clsx('flex w-full flex-col', {
          'lg:flex-row': !singleColumn,
        })}
      >
        <PayoutPlanTextBlock
          variant="primary"
          planType={BLOG_STRINGS['firstOption.type']}
          title={MonthYearRentSliceTitles.firstOption}
          body={<PayoutPlan planType="Monthly" regionId={selectedRegion.id} />}
          description={BLOG_STRINGS['firstOption.description']}
          singleColumn={singleColumn}
        />
        <div className="mt-2sm lg:py-xs flex flex-1 lg:mt-0">
          <PayoutPlanTextBlock
            variant="secondary"
            planType={BLOG_STRINGS['secondOption.type']}
            title={MonthYearRentSliceTitles.secondOption}
            body={<PayoutPlan planType="Yearly" regionId={selectedRegion.id} />}
            description={BLOG_STRINGS['secondOption.description']}
            singleColumn={singleColumn}
          />
        </div>
      </div>
      {selectedRegion.status === 'Active' && (
        <div className="mt-xs">
          <Text variant="p1">
            {template(BLOG_STRINGS.feeRange, {
              minimum: payoutPlan?.Monthly.minimumManagementFeeAmount,
              maximum: payoutPlan?.Monthly.maximumManagementFeeAmount,
            })}
          </Text>
        </div>
      )}
    </div>
  );
}

type PayoutPlanProps = {
  planType: 'Monthly' | 'Yearly';
  regionId?: CoverageRegionModel['id'];
};

function PayoutPlan({ planType, regionId }: PayoutPlanProps): JSX.Element {
  const { regions } = useRegions();
  const selectedRegion = useStore(regionSelector);
  const { payoutPlans } = usePayoutPlans();

  const propRegion = regions.find((region) => region.id === regionId);
  const { status, id } = propRegion || selectedRegion;

  if (!status || !id) {
    return <UnlockPayoutPlanButton />;
  }

  if (!payoutPlans) {
    return (
      <div className="h-xl w-xl relative">
        <Spinner />
      </div>
    );
  }

  let planFeeText = BLOG_STRINGS['feeText.TBD'];
  if (status === 'Active') {
    const planFee =
      payoutPlans[id]?.DEFAULT?.[planType]?.['managementFeePercentage'];
    planFeeText = `${planFee}${BLOG_STRINGS['feeText.active']}`;
  } else if (status === 'ComingSoon') {
    planFeeText = BLOG_STRINGS['feeText.comingSoon'];
  }

  return (
    <Text variant="h3" fontWeight="semibold" className="text-center">
      {planFeeText}
    </Text>
  );
}

function UnlockPayoutPlanButton() {
  const [isHovered, setIsHovered] = useState(false);
  const [
    isRegionSelectModalOpen,
    openRegionSelectModal,
    closeRegionSelectModal,
  ] = useModal();

  return (
    <>
      <ButtonBase onClick={openRegionSelectModal}>
        <div
          className={clsx(
            'border-green bg-green-translucent-light px-xl py-sm duration-hover flex items-center rounded border-2 border-solid transition',
            { 'border-dark-green': isHovered },
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="mr-xs">
            <IconLock width={16} className="text-green" />
          </div>
          <Text
            className={clsx('duration-hover uppercase transition', {
              'text-dark-navy': isHovered,
            })}
            fontWeight="semibold"
          >
            {BLOG_STRINGS.unlock}
          </Text>
        </div>
      </ButtonBase>
      <RegionSelect
        from="default"
        isOpen={isRegionSelectModalOpen}
        onDismiss={closeRegionSelectModal}
      />
    </>
  );
}

type Primary = 'primary';
type Secondary = 'secondary';
type PayoutPlanTextBlockVariant = Primary | Secondary;
type PayoutPlanTextBlockProps = {
  variant: PayoutPlanTextBlockVariant;
  planType: string;
  title: ReactNode;
  body: ReactNode;
  description: string;
  singleColumn?: boolean;
};

function PayoutPlanTextBlock({
  variant,
  planType,
  title,
  body,
  description,
  singleColumn = false,
}: PayoutPlanTextBlockProps): JSX.Element {
  return (
    <div className="pt-md relative flex-1">
      <div
        className={clsx(
          'rounded-small px-xl py-xs absolute top-0',
          css.xCenterAbsoluteContainer,
          { 'bg-navy': variant === 'primary' },
          { 'bg-white border border-solid': variant === 'secondary' },
        )}
      >
        <Text
          variant="h3"
          fontWeight="semibold"
          className={clsx({ 'text-white': variant === 'primary' })}
        >
          {planType}
        </Text>
      </div>
      <div
        className={clsx(
          'border-navy px-sm py-2xl md:px-xl flex size-full flex-col items-center rounded border-solid',
          { 'border-4': variant === 'primary' },
          { border: variant === 'secondary' },
          {
            'lg:border-l-0 lg:rounded-l-none':
              variant === 'secondary' && !singleColumn,
          },
        )}
      >
        <div className="mb-md pt-sm">
          <Text variant="h2" fontWeight="semibold" className="text-center">
            {title}
          </Text>
        </div>
        <div className="mb-md">{body}</div>
        <div
          className={clsx(
            'contents lg:flex lg:items-center',
            css.planTextBlockDescription,
          )}
        >
          <Text className="text-center">{description}</Text>
        </div>
        <div className="pb-sm" />
        <div
          className={clsx(
            'bottom-sm absolute flex items-center',
            css.xCenterAbsoluteContainer,
          )}
        >
          <div className="mr-xs flex-none">
            <IconBank width={24} />
          </div>
          <Text fontWeight="semibold">{BLOG_STRINGS.tax}</Text>
        </div>
      </div>
    </div>
  );
}
